/**
 * Main Content Section Component
 * Renders root containers with insert points and vertical alignment
 */

import { useState } from 'react';
import type { ContainerElement } from '../../types/template.types';
import type { CompanyDetails } from '@/features/company/hooks/use-company-details';
import { ContainerRenderer } from '../renderers/container-renderer';
import { InsertPoint } from '../ui/insert-point';

interface MainContentSectionProps {
  containers: ContainerElement[];
  pageWidth: number;
  pageHeight: number;
  leftMargin: number;
  rightMargin: number;
  topMargin: number;
  bottomMargin: number;
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  onInsertClick: (
    containerPath: number[],
    rowIndex: number,
    position: 'above' | 'below',
    cellRow?: number,
    cellColumn?: number
  ) => void;
  onDeleteElement: (elementId: string) => void;
  companyDetails?: CompanyDetails;
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  rootContainerIds?: string[];
}

export function MainContentSection({
  containers,
  pageWidth,
  pageHeight,
  leftMargin,
  rightMargin,
  topMargin,
  bottomMargin,
  selectedElementId,
  onSelectElement,
  onInsertClick,
  onDeleteElement,
  companyDetails,
  fontFamily,
  fontSize,
  fontColor,
  rootContainerIds = [],
}: MainContentSectionProps) {
  return (
    <div
      className="relative"
      style={{
        padding: `0 ${rightMargin}px 0 ${leftMargin}px`,
        height: `${pageHeight - topMargin - bottomMargin}px`,
        width: pageWidth,
        boxSizing: 'border-box',
        zIndex: 500,
        display: 'grid',
        gridTemplateRows: '1fr',
        alignItems: 'start',
      }}
    >
      {/* Insert point at top if no containers */}
      {containers.length === 0 && (
        <div className="w-full -mt-3">
          <div className="h-6">
            <InsertPoint
              position="above"
              containerPath={[]}
              rowIndex={0}
              onInsert={onInsertClick}
            />
          </div>
          <div className="text-center text-muted-foreground mt-6 text-sm">
            Fahre mit der Maus über die Linie, um dein erstes Element hinzuzufügen
          </div>
        </div>
      )}
      
      {/* Render containers with insert points between */}
      {containers.length > 0 && (
        <div className="relative" style={{ 
          display: 'grid',
          gridTemplateRows: containers.map(c => {
            const verticalAlign = c.verticalAlign;
            const isStretch = verticalAlign === 'stretch';
            // For non-stretch containers with explicit height, use it
            // For non-stretch without height: use minmax(auto, at least 20px) for usability
            if (isStretch) {
              return '1fr';
            } else if (c.height) {
              return `${c.height}px`;
            } else {
              // Dynamic height with minimum for UI usability (plus buttons visible)
              return 'minmax(20px, max-content)';
            }
          }).join(' '),
          height: '100%',
        }}>
          {containers.map((container, index) => {
            // Root containers must always stretch horizontally
            const rootContainer = {
              ...container,
              horizontalAlign: 'stretch' as const,
            };
            
            return (
              <div 
                key={container.id} 
                className="relative"
              >
                {/* Top insert point - only for first container */}
                {index === 0 && (
                  <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: '-12px', height: '24px' }}>
                    <InsertPoint
                      position="above"
                      containerPath={[index]}
                      rowIndex={index}
                      onInsert={onInsertClick}
                    />
                  </div>
                )}
                
                {/* Actual container */}
                <ContainerRenderer
                  container={rootContainer}
                  containerPath={[index]}
                  selectedElementId={selectedElementId}
                  onSelectElement={onSelectElement}
                  onInsertClick={onInsertClick}
                  onDelete={onDeleteElement}
                  companyDetails={companyDetails}
                  fontFamily={fontFamily}
                  fontSize={fontSize}
                  fontColor={fontColor}
                  topMargin={topMargin}
                  bottomMargin={bottomMargin}
                  rootContainerIds={rootContainerIds}
                />
                
                {/* Bottom insert point - after every container */}
                <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ bottom: '-12px', height: '24px' }}>
                  <InsertPoint
                    position="below"
                    containerPath={[index]}
                    rowIndex={index + 1}
                    onInsert={onInsertClick}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
